import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import type { AvatarConfig } from '@/lib/avatar-config';

interface AvatarDisplayProps {
  username?: string | null;
  avatarConfig?: AvatarConfig | null;
  avatarUrl?: string | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showAnimation?: boolean;
  animationType?: 'idle' | 'celebration' | 'thinking';
  className?: string;
}

const sizeClasses = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-16 w-16',
  xl: 'h-24 w-24'
};

export const AvatarDisplay = ({
  username,
  avatarConfig,
  avatarUrl,
  size = 'md',
  showAnimation = false,
  animationType = 'idle',
  className
}: AvatarDisplayProps) => {
  // Generate avatar based on config or use uploaded image
  const getAvatarContent = () => {
    if (avatarUrl) {
      return <AvatarImage src={avatarUrl} alt={username || 'Avatar'} />;
    }

    if (avatarConfig && avatarConfig.colors && avatarConfig.category && avatarConfig.style) {
      // Create animated avatar based on config
      const { category, style, colors, animations } = avatarConfig;
      const animation = showAnimation ? animations?.[animationType] : 'none';
      
      return (
        <div 
          className={cn(
            'w-full h-full rounded-full flex items-center justify-center text-white font-bold overflow-hidden',
            showAnimation && 'animate-float'
          )}
          style={{
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            boxShadow: showAnimation ? `0 0 20px ${colors.accent}40` : undefined
          }}
        >
          {getAvatarEmoji(category, style)}
        </div>
      );
    }

    // Fallback to initials
    const initials = username?.[0]?.toUpperCase() || 'U';
    return (
      <AvatarFallback className="bg-teen-blue text-white font-semibold">
        {initials}
      </AvatarFallback>
    );
  };

  return (
    <Avatar className={cn(sizeClasses[size], className)}>
      {getAvatarContent()}
    </Avatar>
  );
};

// Helper function to get emoji based on category and style
function getAvatarEmoji(category: string, style: string): string {
  const emojiMap: Record<string, Record<string, string>> = {
    'human-male': {
      casual: '😎',
      sporty: '🏃‍♂️', 
      formal: '👨‍💼',
      gamer: '🎮',
      artist: '🎨'
    },
    'human-female': {
      casual: '😊',
      sporty: '🏃‍♀️',
      formal: '👩‍💼', 
      gamer: '🎮',
      artist: '🎨'
    },
    animal: {
      cat: '🐱',
      dog: '🐶',
      fox: '🦊',
      panda: '🐼',
      wolf: '🐺',
      tiger: '🐯',
      rabbit: '🐰',
      owl: '🦉'
    },
    object: {
      robot: '🤖',
      alien: '👽',
      gem: '💎',
      crown: '👑',
      shield: '🛡️',
      lightning: '⚡',
      star: '⭐',
      crystal: '🔮'
    },
    fantasy: {
      wizard: '🧙‍♂️',
      knight: '⚔️',
      dragon: '🐉',
      unicorn: '🦄',
      phoenix: '🔥',
      mage: '🧙‍♀️',
      archer: '🏹',
      warrior: '⚔️'
    }
  };

  return emojiMap[category]?.[style] || '😊';
}